'use client';

import { c as _c } from "react/compiler-runtime";
import { PAGE_TOOLTIP_BODY_CONTAINER_INLINE_CSS, PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS, PAGE_TOOLTIP_FOOTER_CONTAINER_INLINE_CSS, PAGE_TOOLTIP_FOOTER_MARGIN_TOP, PAGE_TOOLTIP_FOOTER_TEXT_INLINE_CSS, PAGE_TOOLTIP_SHELL_INLINE_STYLE, PAGE_TOOLTIP_SHELL_STYLE, PAGE_TOOLTIP_SUBTITLE_INLINE_CSS, PAGE_TOOLTIP_TITLE_INLINE_CSS, tooltipSwatchInlineStyle } from '@/domains/pages/shared/chart-tooltip.config';
import { PAGE_DATA_NUMBER_CLASS, PAGE_DATA_NUMBER_INLINE_CSS } from '@/domains/pages/shared/typography';
import { cn } from '@/utils/shad';
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
const VIEWPORT_PADDING = 8;
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
export function clampFixedTooltipPosition(left: number, top: number, width: number, height: number, padding = VIEWPORT_PADDING): {
  left: number;
  top: number;
} {
  const maxLeft = Math.max(padding, window.innerWidth - width - padding);
  const maxTop = Math.max(padding, window.innerHeight - height - padding);
  return {
    left: clamp(left, padding, maxLeft),
    top: clamp(top, padding, maxTop)
  };
}
export function escapeTooltipHtml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}
type TooltipSwatchLine = {
  color: string;
  label: string;
};
type TooltipFooterLine = {
  text: string;
  color?: string;
};
export function buildTooltipSwatchLineHtml({
  color,
  label
}: TooltipSwatchLine): string {
  return `
    <div style="display:flex;align-items:center;gap:8px;">
      <span style="${tooltipSwatchInlineStyle(color)}"></span>
      <span style="${PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS}">${escapeTooltipHtml(label)}</span>
    </div>
  `;
}
export function buildTooltipFooterLineHtml({
  text,
  color
}: TooltipFooterLine): string {
  const colorStyle = color ? `color:${color};` : '';
  return `<div style="${PAGE_TOOLTIP_FOOTER_TEXT_INLINE_CSS};${colorStyle}">${escapeTooltipHtml(text)}</div>`;
}
export function buildAnalyticsTooltipHtml({
  title,
  subtitle,
  bodyLines = [],
  footerLines = []
}: {
  title: string;
  subtitle?: string;
  bodyLines?: TooltipSwatchLine[];
  footerLines?: TooltipFooterLine[];
}): string {
  const bodyHtml = bodyLines.length > 0 ? `<div style="${PAGE_TOOLTIP_BODY_CONTAINER_INLINE_CSS}">${bodyLines.map(buildTooltipSwatchLineHtml).join('')}</div>` : '';
  const footerHtml = footerLines.length > 0 ? `<div style="${PAGE_TOOLTIP_FOOTER_CONTAINER_INLINE_CSS}">${footerLines.map(buildTooltipFooterLineHtml).join('')}</div>` : '';
  const subtitleHtml = subtitle ? `<div style="${PAGE_TOOLTIP_SUBTITLE_INLINE_CSS}">${escapeTooltipHtml(subtitle)}</div>` : '';
  return `
    <div style="${PAGE_TOOLTIP_SHELL_INLINE_STYLE}">
      <div style="${PAGE_TOOLTIP_TITLE_INLINE_CSS}">${escapeTooltipHtml(title)}</div>${subtitleHtml ? `\n      ${subtitleHtml}` : ''}
      ${bodyHtml}
      ${footerHtml}
    </div>
  `;
}
export type ChartTooltipRow = {
  label: string;
  value: string;
  valueClassName?: string;
  valueStyle?: CSSProperties;
};
type ChartTooltipPanelProps = {
  title?: string;
  titleClassName?: string;
  rows: ChartTooltipRow[];
  description?: string;
  className?: string;
};
export function ChartTooltipPanel(t0) {
  const $ = _c(15);
  const {
    title,
    titleClassName,
    rows,
    description,
    className
  } = t0;
  let t1;
  if ($[0] !== title || $[1] !== titleClassName) {
    t1 = title ? <div className={cn("font-medium text-foreground", titleClassName)} style={{
      fontSize: 12,
      fontWeight: 500,
      marginBottom: 6
    }}>{title}</div> : null;
    $[0] = title;
    $[1] = titleClassName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gap: 2
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== rows) {
    t3 = rows.map(_temp);
    $[4] = rows;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="flex flex-col" style={t2}>{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== description) {
    t5 = description ? <p className="text-muted-foreground" style={{
      marginTop: PAGE_TOOLTIP_FOOTER_MARGIN_TOP,
      marginBottom: 0,
      maxWidth: 224,
      fontSize: 10,
      lineHeight: 1.4
    }}>{description}</p> : null;
    $[8] = description;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== className || $[11] !== t1 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div style={PAGE_TOOLTIP_SHELL_STYLE} className={className}>{t1}{t4}{t5}</div>;
    $[10] = className;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp(row) {
  return <div key={row.label} style={{
    fontSize: 10,
    lineHeight: 1.4
  }}><span className="text-muted-foreground" style={{
      fontStyle: "italic"
    }}>{row.label}</span><span className={cn(PAGE_DATA_NUMBER_CLASS, row.valueClassName ?? "text-foreground")} style={{
      marginLeft: 6,
      fontStyle: "normal",
      ...row.valueStyle
    }}>{row.value}</span></div>;
}
type FloatingTooltipProps = {
  open: boolean;
  x: number;
  y: number;
  children: ReactNode;
};
export function FloatingTooltip(t0) {
  const $ = _c(18);
  const {
    open,
    x,
    y,
    children
  } = t0;
  const contentRef = useRef(null);
  let t1;
  if ($[0] !== x || $[1] !== y) {
    t1 = {
      left: x,
      top: y
    };
    $[0] = x;
    $[1] = y;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [position, setPosition] = useState(t1);
  let t2;
  if ($[3] !== open || $[4] !== x || $[5] !== y) {
    t2 = () => {
      if (!open || !contentRef.current) {
        return;
      }
      const {
        offsetWidth,
        offsetHeight
      } = contentRef.current;
      const anchorLeft = x;
      const anchorTop = y - 12;
      const unclampedLeft = anchorLeft - offsetWidth / 2;
      const unclampedTop = anchorTop - offsetHeight;
      const clamped = clampFixedTooltipPosition(unclampedLeft, unclampedTop, offsetWidth, offsetHeight);
      setPosition(clamped);
    };
    $[3] = open;
    $[4] = x;
    $[5] = y;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== open || $[9] !== x || $[10] !== y) {
    t3 = [open, x, y, children];
    $[7] = children;
    $[8] = open;
    $[9] = x;
    $[10] = y;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useLayoutEffect(t2, t3);
  if (!open) {
    return null;
  }
  let t4;
  if ($[12] !== position.left || $[13] !== position.top) {
    t4 = {
      left: position.left,
      top: position.top
    };
    $[12] = position.left;
    $[13] = position.top;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== children || $[16] !== t4) {
    t5 = <div ref={contentRef} className="pointer-events-none fixed z-9999" style={t4}>{children}</div>;
    $[15] = children;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
export { PAGE_DATA_NUMBER_INLINE_CSS };